import { HashrateItem } from '@components/Charts/ChartDatasetUtils';
import { ReorderInfo } from '@components/global/Reorder2Dv2/matrix';
import { IMinerStatusesModalBase } from '@components/modals/newModals/MinerStatusesModel/MinerStatusesModal.props';
import { MINER_STATUSES_E } from '@constants/MinerStatuses';
import { MODALS_ENUM } from '@constants/Modals';
import { GetStatisticByBuilding_RequestTypes } from '@mobx/api/building';
import { AddRack_RequestTypes, DeleteRack_RequestTypes, EditBuilding_RequestTypes, GetRackList_RequestTypes, MoveRackItem_RequestTypes, OperateRacksHistory_RequestTypes, UpdateRacksPositions_RequestTypes } from '@mobx/api/locations';
import apiService, { createStore, RequestControl, withLoading } from '@mobx/services/http';
import connectionsStore from '@mobx/store/connections';
import { deleteFromMap, setInMap } from '@mobx/utils/maps';
import store_ from '@store';
import dbg from '@utils/dbg';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { Moment } from 'moment';
import { show } from 'redux-modal';
import { getGlobalIntl } from '../../../i18n/ProviderIntl';
import { IBuildingStore, IBuildingStoreBase, IMainLocationsStore, ItemStore } from './interfaces';
import createRackStore from './rack';
import {
  fillLocationRacks3,
  IBuilding,
  IDragRackItemData3,
  IPositionedRackTable3,
  IRack,
  IRackItem3,
  IUpdateRackPosition3,
  MinerStatusInModal,
  MoveType3,
  RACK_SECTION_TYPE3,
  toRackItem3,
  TypePartialRackItem3,
} from './types';
import unallocatedMinersStore from './unallocated';

const DEFAULT_RACK_BLI = "XXX";
const DEFAULT_CELL_BLI = "XX";
const rackPosString = (col: number, row: number) => `${col}_${row}`;

const createBuildingStore_ = (
  locationId: number,
  locationName: string,
  bld: IBuilding,
  mainStore: IMainLocationsStore
) => (rc: RequestControl): IBuildingStore => {
  const getRacks = apiService.createRequest(GetRackList_RequestTypes);
  const addRack = apiService.createRequest(AddRack_RequestTypes);
  const updateRacksPos = apiService.createRequest(UpdateRacksPositions_RequestTypes);
  const deleteRack = apiService.createRequest(DeleteRack_RequestTypes);
  const opRackHistory = apiService.createRequest(OperateRacksHistory_RequestTypes);
  const edit = apiService.createRequest(EditBuilding_RequestTypes)
  const getStatistics = apiService.createRequest(GetStatisticByBuilding_RequestTypes);
  const moveRackItem = apiService.createRequest(MoveRackItem_RequestTypes);
  const { formatMessage } = getGlobalIntl();

  interface IBuildingStore__ extends IBuildingStoreBase {
    fetchRacks_(search?: string, status?: MINER_STATUSES_E): Promise<boolean>;
  }

  interface MaxPos__ {
    maxCol: number;
    maxRow: number;
  }

  const maxPos = (s: MaxPos__, col: number, row: number): MaxPos__ => ({
    maxCol: Math.max(col, s.maxCol),
    maxRow: Math.max(row, s.maxRow),
  });

  let lastTemporaryRackID = -1;

  const self: IBuildingStore__ = {
    building: bld,
    locationId,
    locationName,
    isLoading: false,
    isActive: false,
    isLocked: true,
    isMerging: false,
    isFlipped: false,
    canUndo: false,
    canRedo: false,
    rackStores: new Map,
    items: new Map,
    racksOrder: new Map,
    temporaryRacks: [],
    maxCol: 0,
    maxRow: 0,
    activeCellBLI: DEFAULT_CELL_BLI,
    activeRackBLI: DEFAULT_RACK_BLI,
    isMinersStoreValid: false,
    searchString: undefined,
    statusFilter: undefined,
    activeRackItemId: undefined,
    currentRackTable: undefined,
    activeMinerBLI: undefined,
    activeRack: undefined,

    get racks() {
      return new Map(Array.from(self.rackStores.entries()).map(([id, s]) => [id, s.rack]));
    },

    async fetchRacks_(search?: string, status?: MINER_STATUSES_E) {
      self.searchString = search;
      self.statusFilter = status;
      const handle = rc.createRequestHandle();
      const res = await withLoading(self, getRacks(handle, locationId, bld.id, search, status));

      if (!res || res.Status !== "success" || !res.Records) return false;
      const newRecords = res.Records.map((rack) => {
        const newItems = rack.rack_items.map((item) => {
          const isMinerIdAndPos = (item as IRackItem3).miner_id > 0;
          return {
            ...item,
            pos: isMinerIdAndPos
              ? RACK_SECTION_TYPE3.MINER
              : item.pos === RACK_SECTION_TYPE3.NULL
                ? item.pos
                : RACK_SECTION_TYPE3.EMPTY,
          };
        });
        return { ...rack, rack_items: newItems };
      });

      const r = fillLocationRacks3(newRecords);

      let maxp: MaxPos__ = { maxCol: self.maxCol, maxRow: self.maxRow };

      //dbg.log("RECEIVED", {res, newRecords, r});
      //dbg.log("BEFORE", {...self});

      runInAction(() => {
        self.rackStores = new Map;
        self.items = new Map;
        self.racksOrder = new Map;
        for (const rack of r) {
          const id = +rack.id;
          const pos = rackPosString(rack.rack_pos.col, rack.rack_pos.row);
          maxp = maxPos(maxp, rack.rack_pos.col, rack.rack_pos.row)
          self.racksOrder = setInMap(self.racksOrder, pos, rack.id);
          self.rackStores = setInMap(self.rackStores, id, createRackStore(rack, mainStore));
          for (const item of rack.rack_items) {
            self.items = setInMap(self.items, item.id, item);
          }
        }
        self.isActive = true;
        self.maxCol = maxp.maxCol;
        self.maxRow = maxp.maxRow;
        self.isMinersStoreValid = true;

        //dbg.log("AFTER", {...self});
      });

      return true;
    },

    async search(search?: string, status?: MINER_STATUSES_E) {
      return self.fetchRacks_(search, status);
    },

    async reload() {
      return self.fetchRacks_(self.searchString, self.statusFilter);
    },

    async setActive(active = true) {
      if (active === self.isActive) return true;

      if (!active) { // просто освобождаем память
        self.rackStores = new Map;
        self.items = new Map;
        self.isActive = false;
        return true;
      }

      return self.fetchRacks_(self.searchString, self.statusFilter);
    },

    getRack(col: number, row: number) {
      const pos = rackPosString(col, row);
      const id = self.racksOrder.get(pos);
      if (id !== undefined) return self.racks.get(id);
    },

    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    setCanUndo() {
      self.canRedo = false;
      self.canUndo = true;
    },
    toggleLock() {
      self.isLocked = !self.isLocked;
    },
    setLocked(v) {
      self.isLocked = v;
    },
    toggleMerging() {
      self.isMerging = !self.isMerging;
    },
    toggleFlipped() {
      self.isFlipped = !self.isFlipped;
    },
    setActiveRackItemId(itemId?: number) {
      self.activeRackItemId = itemId;
    },
    setCurrentRack(rack?: IRack) {
      self.currentRackTable = rack;
    },
    setActiveIdentifiers(cell: string, rack: string, miner?: string) {
      self.activeCellBLI = cell;
      self.activeRackBLI = rack;
      self.activeMinerBLI = miner;
    },

    updateSingleRackItem(rackId: number, index: number, item: TypePartialRackItem3) {
      // Получаем стойку, которую нужно обновить
      const rack = self.racks.get(rackId);
      if (rack) {
        const wasID = rack.rack_items[index]?.id;
        // Обновляем элемент в массиве rack_items стойки по индексу
        // Используем splice для замены элемента по заданному индексу
        rack.rack_items.splice(index, 1, item);

        // Если элемент имеет ID, обновляем его и в items
        if (item.id !== undefined) {
          self.items = setInMap(self.items, item.id, item);
          if (wasID && item.id !== wasID) self.items = deleteFromMap(self.items, wasID);
        }
      } else {
        dbg.warn(`Стойка с ID ${rackId} не найдена.`);
      }
    },

    createRackLocally(width: number, height: number, templateID: string) {
      const id = lastTemporaryRackID--;
      const rack: IRack = {
        id,
        rack_id: `${id}`,
        rack_items: [],
        rack_type: [width, height],
      };
      self.temporaryRacks = [...self.temporaryRacks, { rack, templateID }];
      return rack;
    },

    async saveRackToServer(rackID: number, templateID: string, col: number, row: number) {
      const tempRack = self.temporaryRacks.find(tr => tr.rack.id === rackID);
      if (!tempRack) {
        dbg.error(`Temporary rack ${rackID} not found`);
        return;
      }
      const r = await withLoading(self, addRack(rc.createRequestHandle(), locationId, self.building.id, templateID, col, row));
      if (!r) {
        runInAction(() => {
          self.temporaryRacks = self.temporaryRacks.filter(tr => tr.rack.id !== rackID);
        });
        return;
      }

      const createdRack: IPositionedRackTable3 = {
        id: +r.Record.id,
        rack_id: r.Record.rack_id,
        rack_items: r.Record.rack_items,
        rack_type: tempRack.rack.rack_type,
        rack_pos: { row, col },
      };

      runInAction(() => {
        self.rackStores = setInMap(self.rackStores, createdRack.id, createRackStore(createdRack, mainStore));
        const pos = rackPosString(createdRack.rack_pos.col, createdRack.rack_pos.row);
        self.racksOrder = setInMap(self.racksOrder, pos, createdRack.id);
        const maxp = maxPos(self, createdRack.rack_pos.col, createdRack.rack_pos.row);
        self.maxCol = maxp.maxCol;
        self.maxRow = maxp.maxRow;
        self.temporaryRacks = self.temporaryRacks.filter(tr => tr.rack.id !== rackID);
        for (const item of createdRack.rack_items) {
          self.items = setInMap(self.items, item.id, item);
        }
      });

      return createdRack;
    },

    async reorderRacks(info: ReorderInfo[], onNewRackSaved: () => void) {
      const infoMap: Map<number, ReorderInfo> = new Map;
      const oldPosMap: Map<number, string> = new Map;
      for (const el of info) {
        infoMap.set(+el.id, el);
      }

      self.racksOrder.forEach((id, pos) => {
        if (infoMap.has(id)) oldPosMap.set(id, pos);
      });

      const tempRacks = [...self.temporaryRacks];
      const foundIDs = new Set<number>;
      for (const tr of tempRacks) {
        const el = infoMap.get(tr.rack.id);
        if (!el) continue;
        foundIDs.add(tr.rack.id);
        await self.saveRackToServer(tr.rack.id, tr.templateID, el.pos.col, el.pos.row);
        onNewRackSaved();
      }

      let canUndo = false;
      for (const inf of info) {
        const id = +inf.id;
        if (foundIDs.has(id)) continue;
        const rack = self.racks.get(id);
        if (!rack || id < 1) continue;
        const r = await updateRacksPos(rc.createRequestHandle(), { id, x: inf.pos.col + 1, y: inf.pos.row + 1 });
        if (!r) continue;
        canUndo = true;
        const pos = rackPosString(inf.pos.col + 1, inf.pos.row + 1);
        const wasPos = oldPosMap.get(id);
        runInAction(() => {
          if (wasPos) self.racksOrder = deleteFromMap(self.racksOrder, wasPos);
          self.racksOrder = setInMap(self.racksOrder, pos, rack.id);
          self.rackStores = setInMap(
            self.rackStores,
            rack.id,
            createRackStore({ ...rack, rack_pos: inf.pos } as IPositionedRackTable3, mainStore),
          );
        });
      }
      if (canUndo) self.setCanUndo();
    },

    async updateRacksPositions(info: ReorderInfo[]) {
      const infoMap: Map<number, ReorderInfo> = new Map;
      const oldPosMap: Map<number, string> = new Map;
      for (const el of info) {
        infoMap.set(+el.id, el);
      }

      self.racksOrder.forEach((id, pos) => {
        if (infoMap.has(id)) oldPosMap.set(id, pos);
      });

      const ids: IUpdateRackPosition3[] = [];
      for (const inf of info) {
        const rack = self.racks.get(+inf.id);
        if (!rack) continue;
        ids.push({
          id: rack.id,
          x: inf.pos.col + 1,
          y: inf.pos.row + 1,
        });
        const pos = rackPosString(inf.pos.col + 1, inf.pos.row + 1);
        const wasPos = oldPosMap.get(rack.id);
        if (wasPos) deleteFromMap(self.racksOrder, wasPos);
        self.racksOrder = setInMap(self.racksOrder, pos, rack.id);
        self.rackStores = setInMap(
          self.rackStores,
          rack.id,
          createRackStore({ ...rack, rack_pos: inf.pos } as IPositionedRackTable3, mainStore),
        );
      }

      await updateRacksPos(rc.createRequestHandle(), ...ids);
    },

    get hasSearch() {
      return self.searchString !== undefined || self.statusFilter !== undefined;
    },

    deleteRack(rackID: number) {
      return new Promise<boolean>(resolve => {
        const onConfirm = async () => {
          const res = await withLoading(self, deleteRack(rc.createRequestHandle(), rackID));
          if (!res) {
            resolve(false);
            return;
          }
          runInAction(() => {
            self.setCanUndo();
            self.rackStores = new Map(Array.from(self.rackStores).filter(([id]) => id !== rackID));
            const newRacksOrder = new Map(self.racksOrder);
            const posToDelete = new Set<string>();
            self.racksOrder.forEach((id, pos) => {
              if (id === rackID) posToDelete.add(pos);
            });
            posToDelete.forEach(pos => newRacksOrder.delete(pos));
            self.racksOrder = newRacksOrder;
          })
          connectionsStore.getPlaceStore(locationId).removeRacks([rackID]);
          resolve(true);
        };

        store_()?.dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, {
            title: formatMessage({ id: "location.rackTable.rack.sure_delete" }),
            subtitle: formatMessage({ id: "location.rackTable.rack.no_undo" }),
            onConfirm,
          })
        );
      });
    },

    invalidateMinersStore() {
      self.isMinersStoreValid = false;
    },

    async move(typ: MoveType3) {
      const r = await withLoading(self, opRackHistory(rc.createRequestHandle(), typ === "prev"));
      if (!r) return;
      const [canUndo, canRedo] = r.Record;
      runInAction(() => {
        self.canRedo = canRedo;
        self.canUndo = canUndo;
        self.invalidateMinersStore();
        unallocatedMinersStore.invalidateMinersStore();
      })
    },

    removeActiveIdentifiers() {
      self.activeRackBLI = DEFAULT_RACK_BLI;
      self.activeCellBLI = DEFAULT_CELL_BLI;
      self.activeMinerBLI = undefined;
    },

    async rename(name: string) {
      const r = await withLoading(self, edit(rc.createRequestHandle(), self.building.id, name));
      if (!r) return false;
      runInAction(() => {
        self.building = { ...self.building, name };
      });
      return true;
    },

    showStatusesModal(status: MinerStatusInModal, useBuildingId: boolean) {
      const props: IMinerStatusesModalBase = {
        locationId,
        locationName,
        buildingId: useBuildingId ? self.building.id : undefined,
        status,
      }
      store_()?.dispatch(
        show(MODALS_ENUM.MINER_STATUSES_MODAL, props)
      );
    },

    async getStatistics(period: [Moment | null, Moment | null], isAdmin?: boolean): Promise<HashrateItem[]> {
      const response = await withLoading(self, getStatistics(rc.createRequestHandle(), period[0], period[1], self.building.id, isAdmin));
      if (response && response.Status === "success") {
        return response.Record;
      }
      return [];
    },

    async placeAllocatedRackItem(target: ItemStore, data: IDragRackItemData3, sourceRackID: number, targetRackID: number) {
      const r = await moveRackItem(rc.createRequestHandle(), data.id, [target.item.id]);
      if (!r) return false;
      const sourceStore = self.rackStores.get(sourceRackID)!;
      sourceStore.removeItem(data.id);
      const targetStore = self.rackStores.get(targetRackID)!;
      targetStore.placeAllocatedRackItem(target.itemIndex, toRackItem3(r.Record.rack_items[0]), data);
      self.setCanUndo();
      return true;
    },
  };

  const r = makeObservable(self, {
    building: observable,
    locationId: observable,
    isLoading: observable,
    isLocked: observable,
    isActive: observable,
    isMerging: observable,
    isFlipped: observable,
    isMinersStoreValid: observable,
    canUndo: observable,
    canRedo: observable,
    searchString: observable,
    statusFilter: observable,
    activeRackItemId: observable,
    currentRackTable: observable,
    activeCellBLI: observable,
    activeRackBLI: observable,
    activeMinerBLI: observable,
    activeRack: observable,
    items: observable,
    racksOrder: observable,
    temporaryRacks: observable,
    maxCol: observable,
    maxRow: observable,
    rackStores: observable,
    hasSearch: computed,
    racks: computed,
    getRack: action,
    setActive: action,
    reload: action,
    search: action,
    setLoading: action,
    setCanUndo: action,
    toggleLock: action,
    toggleFlipped: action,
    setLocked: action,
    toggleMerging: action,
    setActiveRackItemId: action,
    updateSingleRackItem: action,
    setCurrentRack: action,
    createRackLocally: action,
    saveRackToServer: action,
    reorderRacks: action,
    updateRacksPositions: action,
    deleteRack: action,
    setActiveIdentifiers: action,
    removeActiveIdentifiers: action,
    invalidateMinersStore: action,
    move: action,
    rename: action,
    showStatusesModal: action,
    getStatistics: action,
    placeAllocatedRackItem: action,
  });

  reaction(() => r.isMinersStoreValid, (valid) => !valid && r.reload());

  return r;
};

export const createBuildingStore = (locationId: number, locationName: string, bld: IBuilding, mainStore: IMainLocationsStore) =>
  createStore(createBuildingStore_(locationId, locationName, bld, mainStore));
